// History bridge backed by web_script_versions. Each saveVersion is
// an immutable INSERT — the trim-policy cron (Milestone 5) prunes
// older snapshots so the table can't grow without bound.
//
// Methods mirror the Electron IPC shapes:
//   saveVersion({ filePath, content, manual })  → { ok, id? }
//   listVersions({ filePath })                  → { ok, versions: [...] }
//   getVersion({ filePath, versionId })         → { ok, content, script }

import { buildScript } from '../parsers/fountain.js';
import { announceLocalSave } from './multi-tab.js';

async function currentUserId(supabase) {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
}

async function sha256Hex(text) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function countWords(s) {
  return String(s || '')
    .split(/\s+/)
    .filter(Boolean).length;
}

export function buildHistoryHandlers({ supabase }) {
  return {
    async saveVersion({ filePath: scriptId, content, manual } = {}) {
      if (!supabase || !scriptId || scriptId.startsWith('fixture:')) {
        return { ok: true, skipped: true };
      }
      const userId = await currentUserId(supabase);
      if (!userId) return { ok: false, error: 'not signed in' };
      try {
        const { data: row, error: rErr } = await supabase
          .from('web_scripts')
          .select('active_draft_id')
          .eq('id', scriptId)
          .single();
        if (rErr) throw rErr;
        const draftId = row?.active_draft_id;
        if (!draftId) return { ok: false, error: 'script has no active draft' };

        const text = String(content == null ? '' : content);
        const sha = await sha256Hex(text);

        // Cheap dedupe: don't insert a row if the latest version has
        // the same SHA — keeps the table sparse during quick autosave.
        const { data: latest } = await supabase
          .from('web_script_versions')
          .select('id, content_sha256')
          .eq('draft_id', draftId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (latest && latest.content_sha256 === sha && !manual) {
          return { ok: true, id: latest.id, deduped: true };
        }

        const { data: inserted, error: iErr } = await supabase
          .from('web_script_versions')
          .insert({
            user_id: userId,
            script_id: scriptId,
            draft_id: draftId,
            content: text,
            content_sha256: sha,
            label: manual ? 'manual' : null,
            word_count: countWords(text),
            byte_count: text.length,
            manual: !!manual,
          })
          .select('id')
          .single();
        if (iErr) throw iErr;

        // Bump updated_at on the parent script row so list views sort.
        await supabase
          .from('web_scripts')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', scriptId);

        // Notify any sibling tabs editing the same script so they can
        // surface the stale-content banner if they have a different
        // local sha.
        announceLocalSave(sha);

        return { ok: true, id: inserted.id };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async listVersions({ filePath: scriptId } = {}) {
      if (!supabase || !scriptId || scriptId.startsWith('fixture:')) {
        return { ok: true, versions: [] };
      }
      try {
        const { data, error } = await supabase
          .from('web_script_versions')
          .select('id, label, manual, word_count, byte_count, created_at, draft_id')
          .eq('script_id', scriptId)
          .order('created_at', { ascending: false })
          .limit(200);
        if (error) throw error;
        return {
          ok: true,
          versions: (data || []).map((v) => ({
            id: v.id,
            label: v.label,
            manual: v.manual,
            wordCount: v.word_count,
            byteCount: v.byte_count,
            createdAt: v.created_at,
            draftId: v.draft_id,
          })),
        };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async getVersion({ filePath: scriptId, versionId } = {}) {
      if (!supabase) return { ok: false, error: 'Supabase not configured' };
      if (!scriptId || !versionId) return { ok: false, error: 'scriptId and versionId required' };
      try {
        const { data, error } = await supabase
          .from('web_script_versions')
          .select('content')
          .eq('id', versionId)
          .single();
        if (error) throw error;
        const script = buildScript(data.content, { filePath: scriptId });
        return { ok: true, content: data.content, script };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
  };
}
